/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { WalletError } from '@paysafe/paysafe-wallet-saas-web/errors';

import { FAQ_URL, RETURN_URL_STORAGE_KEY } from '../../constants/checkout-error.const';
import { CheckoutErrorType } from '../../core/enums/checkout-error-type.enum';
import { ErrorMetadata } from '../../models/ui/error-metadata.model';
import { UIStateService } from '../ui-state/ui-state.service';

interface ErrorMapping {
  type: CheckoutErrorType;
  isRetryable: boolean;
  metadata?: Partial<ErrorMetadata>;
}

/**
 * Service to handle checkout errors and map them to appropriate error types
 * Combines HTTP status codes and error codes for precise error handling
 */
@Injectable({
  providedIn: 'root'
})
export class CheckoutErrorHandlerService {

  /**
   * Maps error codes to checkout error types
   * Based on Paysafe Embedded Wallets error codes
   */
  private readonly errorCodeMap: Record<string, ErrorMapping> = {
    // Insufficient funds
    'DW-INSUFFICIENT-FUNDS': {
      type: CheckoutErrorType.INSUFFICIENT_FUNDS,
      isRetryable: false
    },
    // Transfer limits
    'DW-TRANSFER-LIMIT-EXCEEDED': {
      type: CheckoutErrorType.LIMITS_EXCEEDED,
      isRetryable: false,
      metadata: {
        secondaryActionUrl: FAQ_URL
      }
    },
    // Wallet restrictions
    'DW-CUSTOMER-RESTRICTED': {
      type: CheckoutErrorType.WALLET_RESTRICTED,
      isRetryable: false,
      metadata: {
        secondaryActionUrl: FAQ_URL
      }
    },
    // Session/authentication expired
    'DW-SESSION-EXPIRED': {
      type: CheckoutErrorType.PAYMENT_TIMED_OUT,
      isRetryable: false
    },
    // Transaction in progress
    'DW-TRANSFER-IN-PROGRESS': {
      type: CheckoutErrorType.PAYMENT_IN_PROGRESS,
      isRetryable: false
    },
    // Internal error
    1000: {
      type: CheckoutErrorType.TRANSACTION_FAILED,
      isRetryable: false,
      metadata: {
        secondaryActionUrl: FAQ_URL
      }
    },
    // SCA Authentication errors
    'DW-SCA-EVENT-FAILED': {
      type: CheckoutErrorType.SCA_CODE_NOT_SENT,
      isRetryable: false,
      metadata: {
        embeddedErrorPage: true
      }
    },
    'DW-SCA-EVENT-NOT-FOUND': {
      type: CheckoutErrorType.SCA_CODE_NOT_SENT,
      isRetryable: false,
      metadata: {
        embeddedErrorPage: true
      }
    },
    'DW-SCA-CHALLENGE-NOT-FOUND': {
      type: CheckoutErrorType.SCA_CODE_NOT_SENT,
      isRetryable: false,
      metadata: {
        embeddedErrorPage: true
      }
    },
    'DW-SCA-CHALLENGES-EXCEEDED': {
      type: CheckoutErrorType.SCA_TOO_MANY_REQUESTS,
      isRetryable: false,
      metadata: {
        embeddedErrorPage: true
      }
    },
    'DW-SCA-ATTEMPTS-EXCEEDED': {
      type: CheckoutErrorType.SCA_INCORRECT_CODE,
      isRetryable: false,
      metadata: {
        embeddedErrorPage: true
      }
    }
  };

  /**
   * Maps HTTP status codes to default error types (fallback)
   */
  private readonly httpStatusMap: Record<number, ErrorMapping> = {
    400: {
      type: CheckoutErrorType.TRANSACTION_FAILED,
      isRetryable: false,
      metadata: {
        secondaryActionUrl: FAQ_URL
      }
    },
    401: {
      type: CheckoutErrorType.PAYMENT_TIMED_OUT,
      isRetryable: false
    },
    403: {
      type: CheckoutErrorType.WALLET_RESTRICTED,
      isRetryable: false,
      metadata: {
        secondaryActionUrl: FAQ_URL
      }
    }
  };

  // eslint-disable-next-line no-empty-function
  constructor(private uiStateService: UIStateService) {}

  storeReturnUrl(returnUrl: string): void {
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, returnUrl);
  }

  getStoredReturnUrl(): string {
    return sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
  }

  /**
   * Main error handling entry point
   */
  handleError(error: any): void {
    if (error instanceof WalletError) {
      this.handleWalletError(error);
    } else if ((error?.error as WalletError)?.error?.code) {
      this.handleWalletError(error.error as WalletError);
    } else if (error.status) {
      this.handleHttpError(error);
    } else {
      this.handleGenericError();
    }
  }

  /**
   * Handle cancellation (user-initiated)
   * TODO: Consider sending cancellation request to backend if needed
   */
  handleCancellation(): void {
    this.uiStateService.showError(CheckoutErrorType.PAYMENT_CANCELLED);
  }

  /**
   * Handle generic/unknown errors
   */
  handleGenericError(): void {
    this.uiStateService.showError(
      CheckoutErrorType.GENERAL,
      { secondaryActionUrl: FAQ_URL }
    );
  }

  /**
   * Handle SCA error
   */
  handleScaError(error: any, channel: string): void {
    if (error instanceof WalletError) {
      const errorMapping = this.errorCodeMap[error.error.code];
      if (errorMapping && errorMapping.type === CheckoutErrorType.SCA_CODE_NOT_SENT) {
        this.uiStateService.showError(CheckoutErrorType.SCA_CODE_NOT_SENT,
          { subtitle: `Try resending the ${channel} code.`, embeddedErrorPage: true });
        return;
      }
    }
    this.handleError(error);
  }

  /**
   * Handle WalletError from SDK
   * Priority: error code > HTTP status
   */
  private handleWalletError(error: WalletError): void {
    const errorCode = error.error.code;
    const httpStatus = error.error.httpStatus;

    // Try to map by error code first (most specific)
    let mapping = this.errorCodeMap[errorCode];

    // Fallback to HTTP status
    if (!mapping && httpStatus) {
      mapping = this.httpStatusMap[httpStatus];
    }

    // Final fallback
    if (!mapping) {
      mapping = {
        type: CheckoutErrorType.GENERAL,
        isRetryable: true
      };
    }

    this.uiStateService.showError(
      mapping.type,
      mapping.metadata,
      mapping.isRetryable
    );
  }

  /**
   * Handle standard HTTP errors (non-WalletError)
   */
  private handleHttpError(error: any): void {
    const mapping = this.httpStatusMap[error.status] || {
      type: CheckoutErrorType.GENERAL,
      metadata: {
        secondaryActionUrl: FAQ_URL
      },
      isRetryable: false
    };

    this.uiStateService.showError(
      mapping.type,
      mapping.metadata,
      mapping.isRetryable
    );
  }
}
